import { Component } from "../../Component.js";

function resolveValue(value, context = null, ...args) {

    return typeof value === 'function' ? value.call(context, ...args) : value;

}

function normalizeArray(value) {

    if (value === null || value === undefined)
        return [];
    return Array.isArray(value) ? value : [value];

}

function normalizeKey(value) {

    return String(value ?? '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9_.:-]+/g, '-') || 'node';

}

function joinKey(parentNode, localKey) {

    const key = normalizeKey(localKey);
    return parentNode?.key ? `${parentNode.key}/${key}` : key;

}

function getObjectKey(value, fallback) {

    return value?.uid || value?.id || value?.key || value?.name || fallback;

}

function getObjectTitle(value, fallback = 'Object') {

    return value?.name || value?.title || value?.label || value?.uid || value?.id || fallback;

}

export class ObjectTreeNode {

    constructor(options = {}) {

        this.key = options.key || null;
        this.localKey = options.localKey || this.key;
        this.title = options.title || 'Node';
        this.value = options.value ?? null;
        this.parent = options.parent || null;
        this.children = options.children || [];
        this.inspectorTarget = options.inspectorTarget || null;
        this.actions = options.actions || [];
        this.metadata = options.metadata || null;
        this.expanded = options.expanded ?? false;
        this.expandable = options.expandable ?? false;
        this.factory = options.factory || null;

    }

    isInspectable() {

        return !!this.inspectorTarget;

    }

    hasChildren() {

        return this.children.length > 0;

    }

    isExpandable() {

        return this.expandable || this.hasChildren();

    }

}

export class ObjectTreeNodeFactory {

    constructor(options = {}) {

        this.key = options.key || null;
        this.title = options.title || null;
        this.value = options.value ?? null;
        this.children = options.children || [];
        this.inspectorTarget = options.inspectorTarget || null;
        this.actions = options.actions || [];
        this.metadata = options.metadata || null;
        this.expanded = options.expanded ?? false;
        this.expandable = options.expandable ?? false;

    }

    createNodes(context = {}, parentNode = null) {

        return [this.createNode(context, parentNode)];

    }

    createNode(context = {}, parentNode = null, overrides = {}) {

        const localKey = resolveValue(overrides.key ?? this.key, this, context, parentNode, overrides.value) || resolveValue(overrides.title ?? this.title, this, context, parentNode, overrides.value) || 'node';
        const node = new ObjectTreeNode({
            key: joinKey(parentNode, localKey),
            localKey,
            title: resolveValue(overrides.title ?? this.title, this, context, parentNode, overrides.value) || 'Node',
            value: resolveValue(overrides.value ?? this.value, this, context, parentNode),
            parent: parentNode,
            inspectorTarget: resolveValue(overrides.inspectorTarget ?? this.inspectorTarget, this, context, parentNode, overrides.value) || null,
            actions: normalizeArray(resolveValue(overrides.actions ?? this.actions, this, context, parentNode, overrides.value)),
            metadata: resolveValue(overrides.metadata ?? this.metadata, this, context, parentNode, overrides.value) || null,
            expanded: resolveValue(overrides.expanded ?? this.expanded, this, context, parentNode, overrides.value) === true,
            expandable: resolveValue(overrides.expandable ?? this.expandable, this, context, parentNode, overrides.value) === true,
            factory: this,
        });

        node.children = this.createChildNodes(context, node);
        return node;

    }

    createChildNodes(context = {}, parentNode = null) {

        return normalizeArray(resolveValue(this.children, this, context, parentNode))
            .flatMap((childFactory) => childFactory instanceof ObjectTreeNodeFactory
                ? childFactory.createNodes(context, parentNode)
                : []);

    }

}

export class StaticObjectTreeNodeFactory extends ObjectTreeNodeFactory {
}

export class CollectionObjectTreeNodeFactory extends ObjectTreeNodeFactory {

    constructor(options = {}) {

        super(options);

        this.expandable = options.expandable ?? true;
        this.items = options.items || options.getItems || [];
        this.itemFactory = options.itemFactory || new ValueObjectTreeNodeFactory();
        this.itemContextName = options.itemContextName || 'value';

    }

    createChildNodes(context = {}, parentNode = null) {

        const staticChildren = super.createChildNodes(context, parentNode);
        const items = normalizeArray(resolveValue(this.items, this, context, parentNode));
        const itemChildren = items.flatMap((item, index) => this.itemFactory.createNodes({
            ...context,
            [this.itemContextName]: item,
            item,
            index,
            collectionNode: parentNode,
        }, parentNode));

        return [...staticChildren, ...itemChildren];

    }

}

export class ValueObjectTreeNodeFactory extends ObjectTreeNodeFactory {

    constructor(options = {}) {

        super(options);

        this.getValue = options.getValue || ((context) => context.value ?? context.item ?? null);
        this.getKey = options.getKey || ((value, context) => getObjectKey(value, context.index ?? 'item'));
        this.getTitle = options.getTitle || ((value, context) => getObjectTitle(value, `Item ${context.index + 1}`));
        this.getInspectorTarget = options.getInspectorTarget || ((value) => value ? { value } : null);

    }

    createNode(context = {}, parentNode = null, overrides = {}) {

        const value = resolveValue(overrides.value, this, context, parentNode) ?? this.getValue(context, parentNode);

        return super.createNode(context, parentNode, {
            ...overrides,
            key: overrides.key ?? this.getKey(value, context, parentNode),
            title: overrides.title ?? this.getTitle(value, context, parentNode),
            value,
            inspectorTarget: overrides.inspectorTarget ?? this.getInspectorTarget(value, context, parentNode),
        });

    }

}

export class CallbackObjectTreeNodeFactory extends ObjectTreeNodeFactory {

    constructor(options = {}) {

        super(options);

        this.createNodesCallback = options.createNodes || options.callback || null;

    }

    createNodes(context = {}, parentNode = null) {

        const result = typeof this.createNodesCallback == 'function'
            ? this.createNodesCallback.call(this, context, parentNode)
            : [];

        return normalizeArray(result).flatMap((item) => {
            if (item instanceof ObjectTreeNodeFactory)
                return item.createNodes(context, parentNode);
            if (item instanceof ObjectTreeNode)
                return [item];
            if (item && typeof item == 'object')
                return [new ObjectTreeNode({ ...item, parent: item.parent || parentNode })];
            return [];
        });

    }

}

export class ObjectTree extends Component {

    constructor(parent, options = {}) {

        options = Object.assign({
            rootFactory: null,
            factories: null,
            context: null,
            selectedKey: null,
            expandedKeys: null,
            onNodeSelect: null,
            emptyText: 'No tree nodes available.',
        }, options || {});

        super(parent, options);

        this.rootFactory = options.rootFactory;
        this.factories = options.factories;
        this.context = options.context;
        this.selectedKey = options.selectedKey;
        this.expandedKeys = new Set(options.expandedKeys || []);
        this.collapsedKeys = new Set();
        this.onNodeSelect = options.onNodeSelect;
        this.emptyText = options.emptyText;

    }

    getContext() { return resolveValue(this.context, this) || {}; }

    getSelectedKey() { return resolveValue(this.selectedKey, this); }

    getRootFactories() {

        if (this.rootFactory)
            return normalizeArray(resolveValue(this.rootFactory, this));
        return normalizeArray(resolveValue(this.factories, this));

    }

    getNodes() {

        const context = this.getContext();
        return this.getRootFactories()
            .flatMap((factory) => factory instanceof ObjectTreeNodeFactory
                ? factory.createNodes(context, null)
                : []);

    }

    isNodeExpanded(node) {

        if (!node?.isExpandable() || !node.hasChildren())
            return false;
        if (this.collapsedKeys.has(node.key))
            return false;
        return this.expandedKeys.has(node.key) || node.expanded === true;

    }

    toggleNode(node, vm) {

        if (!node?.isExpandable() || !node.hasChildren())
            return;

        if (this.isNodeExpanded(node)) {
            this.expandedKeys.delete(node.key);
            this.collapsedKeys.add(node.key);
        }
        else {
            this.collapsedKeys.delete(node.key);
            this.expandedKeys.add(node.key);
        }

        if (vm && typeof vm.invalidate == 'function')
            vm.invalidate();
        else
            this.invalidate();

    }

    selectNode(node, vm) {

        if (!node)
            return;

        const result = typeof this.onNodeSelect == 'function'
            ? this.onNodeSelect.call(this, node)
            : undefined;

        if (result !== false)
            this.selectedKey = node.key;

        if (vm && typeof vm.invalidate == 'function')
            vm.invalidate();
        else
            this.invalidate();

    }

    _renderNode(r, vm, node) {

        const expanded = this.isNodeExpanded(node);
        const expandable = node.isExpandable();
        const hasChildren = node.hasChildren();
        const selected = this.getSelectedKey() == node.key;
        const css = [
            'object-tree__node',
            selected ? 'active object-tree__node--selected' : null,
            node.isInspectable() ? 'object-tree__node--inspectable' : null,
        ].filter(Boolean).join(' ');

        return r.menuItem(null, null, {
            css,
            props: {
                title: node.title,
                onclick: (event) => {
                    event?.preventDefault?.();
                    event?.stopPropagation?.();
                    this.selectNode(node, vm)
                },
            },
        },
            hasChildren && expanded ? this._renderNodeList(r, vm, node.children, expanded) : null,
            [
                (expandable
                    ? r.iconButton(
                        r.icon('chevron-right', (expanded ? 'fa-rotate-90' : '')),
                        {
                            css: 'object-tree__toggle circle transparent',
                            props: {
                                title: hasChildren ? (expanded ? 'Collapse node' : 'Expand node') : 'No items to expand',
                                disabled: !hasChildren,
                                onclick: (event) => {
                                    event?.preventDefault?.();
                                    event?.stopPropagation?.();
                                    this.toggleNode(node, vm);
                                },
                            },
                        }
                    )
                    : r.span('object-tree__toggle object-tree__toggle--placeholder', [''])
                ),
                r.span({ css: 'object-tree__title max', }, [node.title]),
            ]
        );

    }

    _renderNodeList(r, vm, nodes, expanded = false) {

        return r.menu({ css: 'object-tree__list' + (expanded ? ' active' : '') }, nodes.map((node) => this._renderNode(r, vm, node)));

    }

    _render(r, vm) {

        const nodes = this.getNodes();
        const css = this.getCss() ?? null;

        if (!nodes.length) {
            return r.div({ css: `object-tree ${css}` }, [
                r.empty({ title: this.emptyText, icon: '#info' }),
            ]);
        }

        return r.div({ css: `object-tree ${css}` }, [
            this._renderNodeList(r, vm, nodes),
        ]);

    }

}
